/**
 * The register's aggregate statistics - a dialog of charts, opened from the page
 * head. It gives a bird's eye view of the whole mirror: legal forms (how many
 * private limited companies, sole traders, non-profits…), profit vs loss (the
 * latest report's net result), status and counties, plus KPI tiles (total · VAT ·
 * e-mail · with a report). Data: `GET /admin/ariregister/stats` (cached on the
 * server). The charts are drawn by the shadcn/ui chart library (Recharts).
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  BarChart3, Building2, Mail, Receipt, FileText, Loader2, MapPinned,
  Globe, CalendarPlus, Wallet, ArrowRight,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, XAxis, YAxis } from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  getStats, getCountyStats, legalFormShort, COUNTIES, COUNTY_NULL,
  type ArStats, type ArCountyStats,
} from "../../../lib/ariregister";
import { sectionForDivision } from "../../../lib/emtak";

type T = (k: string, vars?: Record<string, string | number>) => string;

/** Lead presets - clicking one applies the matching filters to the list. */
export type ProspectPreset = "noSiteEmail" | "noSiteRevenue" | "newNoSite";

const fmt = (n: number) => n.toLocaleString("et-EE");
const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "-");
const trunc = (s: string, max = 26) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/** The categorical bar palette - distinguishable oklch tones near the brand. */
const PALETTE = [
  "oklch(0.55 0.20 268)", // indigo (primary)
  "oklch(0.65 0.22 285)", // violet
  "oklch(0.62 0.16 200)", // teal
  "oklch(0.62 0.16 155)", // green
  "oklch(0.78 0.16 75)", // amber
  "oklch(0.62 0.22 25)", // red
  "oklch(0.60 0.18 330)", // pink
  "oklch(0.55 0.14 240)", // blue
  "oklch(0.64 0.15 130)", // lime
  "oklch(0.58 0.012 270)", // grey (other)
];

const BAR_CONFIG = { count: { label: "Ettevõtteid" } } satisfies ChartConfig;

/** One tooltip row: "label: count" - the full label when there is one, otherwise
 *  the axis label, with the value in mono. */
function rowTooltip(value: unknown, _name: unknown, item: { payload?: { label?: string; full?: string } }) {
  const p = item?.payload;
  return (
    <span className="flex w-full items-center justify-between gap-3">
      <span className="text-muted-foreground">{p?.full ?? p?.label}</span>
      <span className="font-mono font-medium tabular-nums text-foreground">{fmt(Number(value))}</span>
    </span>
  );
}

type Row = { key: string; label: string; count: number; fill: string; full?: string };

/** Top N plus an "other" roll-up, for the shared horizontal bar chart. */
function topRows(
  items: { label: string; count: number }[],
  topN: number,
  muuLabel: string,
): Row[] {
  const top = items.slice(0, topN).map((r, i) => ({ key: `r${i}`, label: r.label, count: r.count, fill: PALETTE[i % PALETTE.length]! }));
  const restSum = items.slice(topN).reduce((s, r) => s + r.count, 0);
  if (restSum > 0) top.push({ key: "muu", label: muuLabel, count: restSum, fill: PALETTE[9]! });
  return top;
}

/** The shared horizontal bar chart (legal forms · counties · status · activities). */
function HBars({ rows, height, labelWidth = 84 }: { rows: Row[]; height: number; labelWidth?: number }) {
  return (
    <ChartContainer config={BAR_CONFIG} className="aspect-auto w-full" style={{ height }}>
      <BarChart accessibilityLayer layout="vertical" data={rows} margin={{ left: 4, right: 52, top: 2, bottom: 2 }}>
        <XAxis type="number" dataKey="count" hide />
        <YAxis type="category" dataKey="label" tickLine={false} axisLine={false} width={labelWidth} tick={{ fontSize: 12 }} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel formatter={rowTooltip} />} />
        <Bar dataKey="count" radius={5} maxBarSize={24}>
          {rows.map((r) => (
            <Cell key={r.key} fill={r.fill} />
          ))}
          <LabelList dataKey="count" position="right" offset={6} className="fill-muted-foreground" fontSize={11} formatter={(v: number) => fmt(v)} />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

function KpiTile({ icon: Icon, label, value, sub }: { icon: typeof Building2; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Panel({ title, icon: Icon, children }: { title: string; icon?: typeof Building2; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card/60 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {Icon && <Icon className="size-4 text-primary" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The profit and loss donut plus its legend (category · count · share). */
function ProfitDonut({ profit, t }: { profit: ArStats["profit"]; t: T }) {
  const segs = [
    { key: "profit", label: t("ariregister.stats.profit"), count: profit.profit, fill: "var(--color-success)" },
    { key: "loss", label: t("ariregister.stats.loss"), count: profit.loss, fill: "var(--color-destructive)" },
    { key: "zero", label: t("ariregister.stats.zero"), count: profit.zero, fill: "var(--color-warning)" },
    { key: "noReport", label: t("ariregister.stats.noReport"), count: profit.noReport, fill: "oklch(0.72 0.01 270)" },
  ].filter((x) => x.count > 0);
  const total = segs.reduce((sum, x) => sum + x.count, 0);

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <ChartContainer config={BAR_CONFIG} className="aspect-square h-[170px] shrink-0">
        <PieChart>
          <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel formatter={rowTooltip} />} />
          <Pie data={segs} dataKey="count" nameKey="label" innerRadius={48} outerRadius={80} strokeWidth={3} paddingAngle={2}>
            {segs.map((seg) => (
              <Cell key={seg.key} fill={seg.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <ul className="w-full space-y-1.5 text-sm">
        {segs.map((seg) => (
          <li key={seg.key} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: seg.fill }} />
            <span className="flex-1 text-muted-foreground">{seg.label}</span>
            <span className="font-mono font-medium tabular-nums">{fmt(seg.count)}</span>
            <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">{pct(seg.count, total)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A clickable lead tile - applies the list filters and closes the dialog. */
function ProspectTile({ icon: Icon, label, value, hint, onClick }: { icon: typeof Globe; label: string; value: number; hint: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="group flex flex-col rounded-lg border border-border bg-background/60 p-3 text-left transition-colors enabled:hover:border-primary/50 enabled:hover:bg-primary/5 disabled:cursor-default"
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums">{fmt(value)}</div>
      {onClick && (
        <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
          {hint} <ArrowRight className="size-3" />
        </span>
      )}
    </button>
  );
}

/** Web presence plus qualified leads - who a web developer should be calling. */
function WebProspects({ s, onApply, t }: { s: ArStats; onApply?: (p: ProspectPreset) => void; t: T }) {
  const noSite = Math.max(0, s.active - s.web.withSite);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
        <KpiTile icon={Globe} label={t("ariregister.stats.withSite")} value={fmt(s.web.withSite)} sub={pct(s.web.withSite, s.active)} />
        <KpiTile icon={Globe} label={t("ariregister.stats.noSite")} value={fmt(noSite)} sub={pct(noSite, s.active)} />
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <ProspectTile icon={Mail} label={t("ariregister.stats.noSiteEmail")} value={s.prospects.noSiteEmail} hint={t("ariregister.stats.viewList")} onClick={onApply && (() => onApply("noSiteEmail"))} />
        <ProspectTile icon={Wallet} label={t("ariregister.stats.noSiteRevenue")} value={s.prospects.noSiteEmailRevenue} hint={t("ariregister.stats.viewList")} onClick={onApply && (() => onApply("noSiteRevenue"))} />
        <ProspectTile icon={CalendarPlus} label={t("ariregister.stats.newNoSite")} value={s.prospects.newLast12moNoSite} hint={t("ariregister.stats.viewList")} onClick={onApply && (() => onApply("newNoSite"))} />
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">{t("ariregister.stats.webCaveat")}</p>
    </div>
  );
}

/** New companies by month over the last 24 months, as a vertical bar chart. */
function NewCompaniesTrend({ data, t }: { data: ArStats["newByMonth"]; t: T }) {
  if (data.length < 2) return <p className="text-sm text-muted-foreground">-</p>;
  const rows = data.map((d) => ({ month: d.month.slice(2), count: d.count }));
  const config = { count: { label: t("ariregister.stats.newCompanies"), color: "var(--color-primary)" } } satisfies ChartConfig;
  return (
    <ChartContainer config={config} className="aspect-auto w-full" style={{ height: 190 }}>
      <BarChart accessibilityLayer data={rows} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} interval={2} tick={{ fontSize: 10 }} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
        <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ChartContainer>
  );
}

/** Tax office revenue bands as bars, in size order rather than by frequency. */
function revenueBandRows(bands: ArStats["revenueBands"], t: T): Row[] {
  const meta: Record<string, { label: string; fill: string }> = {
    lt50k: { label: t("ariregister.stats.revLt50k"), fill: PALETTE[2]! },
    b50_200: { label: t("ariregister.stats.rev50_200"), fill: PALETTE[3]! },
    b200_1m: { label: t("ariregister.stats.rev200_1m"), fill: PALETTE[4]! },
    gte1m: { label: t("ariregister.stats.revGte1m"), fill: PALETTE[0]! },
    none: { label: t("ariregister.stats.revNone"), fill: PALETTE[9]! },
  };
  const order = ["lt50k", "b50_200", "b200_1m", "gte1m", "none"];
  const byKey = new Map(bands.map((b) => [b.band, b.count]));
  return order
    .filter((k) => (byKey.get(k) ?? 0) > 0)
    .map((k) => ({ key: k, label: meta[k]!.label, count: byKey.get(k) ?? 0, fill: meta[k]!.fill }));
}

/** Two-digit EMTAK divisions rolled up to section-level bars (top 8 plus other).
 *  An unknown section becomes `unknownLabel`, the remaining tail `otherLabel`. */
function sectionRows(divisions: { div: string; count: number }[], unknownLabel: string, otherLabel: string): Row[] {
  const by = new Map<string, { name: string; count: number }>();
  for (const d of divisions) {
    const sec = sectionForDivision(d.div);
    const key = sec?.letter ?? "_";
    const name = sec?.name ?? unknownLabel;
    const cur = by.get(key) ?? { name, count: 0 };
    cur.count += d.count;
    by.set(key, cur);
  }
  const sorted = [...by.values()].sort((a, b) => b.count - a.count);
  const rows: Row[] = sorted.slice(0, 8).map((s, i) => ({
    key: `s${i}`, label: trunc(s.name), full: s.name, count: s.count, fill: PALETTE[i % PALETTE.length]!,
  }));
  const rest = sorted.slice(8).reduce((n, s) => n + s.count, 0);
  if (rest > 0) rows.push({ key: "muu", label: otherLabel, count: rest, fill: PALETTE[9]! });
  return rows;
}

/** The county drill-down, loaded lazily: pick a county, get its activities and
 *  its profit and loss split. */
function CountyDrill({ t }: { t: T }) {
  const [county, setCounty] = useState("");
  const [data, setData] = useState<ArCountyStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!county) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(false);
    setData(null);
    getCountyStats(county)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [county]);

  const secRows = data ? sectionRows(data.divisions, t("ariregister.stats.unknown"), t("ariregister.stats.other")) : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={county} onValueChange={setCounty}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t("ariregister.stats.selectCounty")} />
          </SelectTrigger>
          <SelectContent>
            {COUNTIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
            <SelectItem value={COUNTY_NULL}>{t("ariregister.stats.unknown")}</SelectItem>
          </SelectContent>
        </Select>
        {data && <span className="text-xs text-muted-foreground">{t("ariregister.stats.countyTotal", { n: fmt(data.total) })}</span>}
      </div>

      {!county ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("ariregister.stats.countyHint")}</p>
      ) : loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t("ariregister.loading")}
        </div>
      ) : error ? (
        <p className="py-6 text-center text-sm text-destructive">{t("ariregister.stats.error")}</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <KpiTile icon={Globe} label={t("ariregister.stats.withSite")} value={fmt(data.withSite)} sub={pct(data.withSite, data.total)} />
            <KpiTile icon={Globe} label={t("ariregister.stats.noSite")} value={fmt(Math.max(0, data.total - data.withSite))} sub={pct(Math.max(0, data.total - data.withSite), data.total)} />
            <KpiTile icon={CalendarPlus} label={t("ariregister.stats.newLast12mo")} value={fmt(data.newLast12mo)} sub={pct(data.newLast12mo, data.total)} />
            <KpiTile icon={FileText} label={t("ariregister.stats.kpiReport")} value={fmt(data.withReport)} sub={pct(data.withReport, data.total)} />
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h4 className="mb-2 text-xs font-medium text-muted-foreground">{t("ariregister.stats.countyActivities")}</h4>
              {secRows.length ? (
                <HBars rows={secRows} height={Math.max(150, secRows.length * 30)} labelWidth={168} />
              ) : (
                <p className="text-sm text-muted-foreground">-</p>
              )}
            </div>
            <div>
              <h4 className="mb-2 text-xs font-medium text-muted-foreground">{t("ariregister.stats.byProfit")}</h4>
              <ProfitDonut profit={data.profit} t={t} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function StatsBody({ s, t, onApply }: { s: ArStats; t: T; onApply?: (p: ProspectPreset) => void }) {
  const legalRows = topRows(
    s.legalForms.map((r) => ({ label: legalFormShort(r.legalForm), count: r.count })),
    9,
    t("ariregister.stats.other"),
  );
  const revRows = revenueBandRows(s.revenueBands, t);
  const countyRows = topRows(
    s.counties.map((r) => ({ label: r.county ? r.county.replace(/\s*maakond$/i, "") : t("ariregister.stats.unknown"), count: r.count })),
    9,
    t("ariregister.stats.other"),
  );
  const statusRows: Row[] = s.statuses
    .filter((r) => r.status)
    .map((r, i) => ({
      key: r.status ?? `s${i}`,
      label: r.statusText ?? r.status ?? "-",
      count: r.count,
      fill: r.status === "R" ? "var(--color-success)" : r.status === "L" ? "var(--color-warning)" : r.status === "N" ? "var(--color-destructive)" : PALETTE[9]!,
    }));

  return (
    <div className="space-y-4">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <KpiTile icon={Building2} label={t("ariregister.stats.kpiTotal")} value={fmt(s.total)} sub={t("ariregister.stats.kpiActive", { n: fmt(s.active) })} />
        <KpiTile icon={Receipt} label={t("ariregister.stats.kpiVat")} value={fmt(s.vat)} sub={pct(s.vat, s.active)} />
        <KpiTile icon={Mail} label={t("ariregister.stats.kpiEmail")} value={fmt(s.email)} sub={pct(s.email, s.active)} />
        <KpiTile icon={FileText} label={t("ariregister.stats.kpiReport")} value={fmt(s.withReport)} sub={pct(s.withReport, s.active)} />
      </div>

      {/* Sales targeting: web presence plus qualified no-website leads */}
      <Panel title={t("ariregister.stats.webProspects")} icon={Globe}>
        <WebProspects s={s} onApply={onApply} t={t} />
      </Panel>

      {/* New companies by month - the fresh lead stream */}
      <Panel title={t("ariregister.stats.newTrend")} icon={CalendarPlus}>
        <NewCompaniesTrend data={s.newByMonth} t={t} />
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t("ariregister.stats.byRevenue")}>
          <HBars rows={revRows} height={Math.max(120, revRows.length * 32)} labelWidth={96} />
        </Panel>
        <Panel title={t("ariregister.stats.byLegalForm")}>
          <HBars rows={legalRows} height={Math.max(160, legalRows.length * 30)} />
        </Panel>
        <Panel title={t("ariregister.stats.byProfit")}>
          <ProfitDonut profit={s.profit} t={t} />
        </Panel>
        <Panel title={t("ariregister.stats.byStatus")}>
          <HBars rows={statusRows} height={Math.max(120, statusRows.length * 34)} />
        </Panel>
        <Panel title={t("ariregister.stats.byCounty")}>
          <HBars rows={countyRows} height={Math.max(160, countyRows.length * 30)} />
        </Panel>
      </div>

      {/* The county drill-down, loaded lazily - only the picker at first */}
      <Panel title={t("ariregister.stats.countyDrill")} icon={MapPinned}>
        <CountyDrill t={t} />
      </Panel>
    </div>
  );
}

export function AriregisterStats({
  onApplyProspect,
  children,
}: {
  onApplyProspect?: (p: ProspectPreset) => void;
  /**
   * The control that opens the dialog. Handed in rather than built here so the
   * button can wear whatever the surface it stands on wears - this one lives in
   * the page head beside the CRM's own pill buttons, and the shared `Button`
   * this used to render was two sizes and one shape away from its neighbours.
   */
  children?: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<ArStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // A click on a lead tile applies the list filters and closes the dialog.
  const applyProspect = onApplyProspect
    ? (p: ProspectPreset) => {
        onApplyProspect(p);
        setOpen(false);
      }
    : undefined;

  // Loaded lazily on the first opening and then kept - the server caches it anyway.
  useEffect(() => {
    if (!open || stats || loading) return;
    setLoading(true);
    setError(false);
    getStats()
      .then(setStats)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, stats, loading]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ?? (
          <button type="button" className="crm-btn">
            <BarChart3 className="size-4" /> {t("ariregister.stats.open")}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="size-5 text-primary" /> {t("ariregister.stats.title")}
          </DialogTitle>
          <DialogDescription>{t("ariregister.stats.subtitle")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t("ariregister.loading")}
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-destructive">{t("ariregister.stats.error")}</div>
        ) : stats ? (
          <StatsBody s={stats} t={t} onApply={applyProspect} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
